'use client'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2 } from 'lucide-react'
import { formatCredits } from '@/lib/billing/utils/credits'
import type { ShopifyTopupOption } from '@/lib/billing/topup/shopify-topup-service'
import { TopupLifetimeNote } from './topup-lifetime-note'

export function ShopifyTopupDialog({
  option,
  open,
  onOpenChange,
  onConfirm,
  pending,
  lifetimeMonths,
}: {
  option: ShopifyTopupOption | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  pending: boolean
  lifetimeMonths: number | null
}) {
  if (!option) return null

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><span>Add</span> {formatCredits(option.creditMicrousd)} <span>to your balance?</span></AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                {option.label} <span>will be added to your store&apos;s next Shopify invoice, and</span>{' '}
                {formatCredits(option.creditMicrousd)} <span>lands on your balance right away</span>
                <span>{option.bonusPercent > 0 && ` (includes your ${option.bonusPercent}% plan bonus)`}</span><span>.</span>
              </p>
              <p>Shopify bills it with your subscription at the end of the billing cycle.</p>
              <TopupLifetimeNote months={lifetimeMonths} />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              // Radix closes on click; the dialog has to stay up while the charge runs.
              event.preventDefault()
              onConfirm()
            }}
          >
            {pending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <span>Confirm</span> {option.label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
